import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WindowService } from '@plys/src/ui/window/window.service';

export class TooltipElementNotFoundError extends Error {
  message = 'Tooltip element cannot be found.';
}

export class TooltipContainerNotFoundError extends Error {
  message = 'Tooltip container cannot be found';
}

@Injectable({
  providedIn: 'root',
})
export class TooltipService {
  private static readonly SHOW_CSS_CLASS = 'show';

  constructor(@Inject(DOCUMENT) private doc: Document, private snackbar: MatSnackBar, private window: WindowService) {}

  showById(selector: string, id: string, containerSelector: string): void {
    const target = this.doc.getElementById(id);

    this.show(selector, target, containerSelector);
  }

  show(selector: string, target: HTMLElement, containerSelector: string): void {
    const tooltip = this.doc.querySelector<HTMLElement>(selector);

    if (!tooltip) {
      throw new TooltipElementNotFoundError();
    }

    if (this.window.isMobile()) {
      const msg = tooltip.innerHTML;
      this.snackbar.open(msg, 'OK', {
        duration: 2000,
      });
      return;
    }

    const container = this.defineContainer(containerSelector);

    const { left, top } = this.calculateTooltipPosition(target, tooltip, container);

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
    tooltip.classList.add(TooltipService.SHOW_CSS_CLASS);
  }

  hide(selector: string): void {
    const tooltip = this.doc.querySelector<HTMLElement>(selector);

    if (tooltip) {
      tooltip.classList.remove(TooltipService.SHOW_CSS_CLASS);
      tooltip.style.top = '0px';
      tooltip.style.left = '0px';
    }
  }

  private defineContainer(containerSelector: string): HTMLElement {
    const container = this.doc.querySelector<HTMLElement>(containerSelector);

    if (!container) {
      throw new TooltipContainerNotFoundError();
    }

    return container;
  }

  private calculateTooltipPosition(
    target: HTMLElement,
    tooltip: HTMLElement,
    container: HTMLElement,
    tooltipSpacing = 10
  ): { left: number; top: number } {
    const targetRect = target.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const targetLeftRelativeToContainer = targetRect.left - containerRect.left;
    const targetTopRelativeToContainer = targetRect.top - containerRect.top;
    const left = targetLeftRelativeToContainer - tooltip.clientWidth / 2 + targetRect.width / 2;
    const top = targetTopRelativeToContainer - tooltip.clientHeight - tooltipSpacing;
    return { left, top };
  }
}
